/**
 * ImportedObjGeometry — draws the parsed Wavefront OBJ mesh payload of a
 * BIM 'Imported OBJ' element inside the 3D workbench. The parser
 * (services/obj/objParser) already applied the Y-up → Z-up axis conversion
 * and quad split; this component only rebuilds THREE.BufferGeometry objects
 * from the flat vertex/index arrays and shades them like every other link
 * category (selection overlays ink edges).
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { InkEdges } from './InkEdges';
import { BACKFACE_DEFINES } from './backfaceTint';

export interface ObjPartPayload {
    name?: string;
    positions: number[];
    normals?: number[];
    indices?: number[];
}

const OBJ_MESH_COLOR = '#9aa0a6';

export function ImportedObjGeometry({ parts, isSelected }: { parts: ObjPartPayload[]; isSelected: boolean }) {
    const geometries = useMemo(() => parts.map((part) => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(part.positions, 3));
        if (part.normals && part.normals.length === part.positions.length) {
            geo.setAttribute('normal', new THREE.Float32BufferAttribute(part.normals, 3));
        } else {
            geo.computeVertexNormals();
        }
        if (part.indices && part.indices.length > 0) geo.setIndex(part.indices);
        geo.computeBoundingSphere();
        return geo;
    }), [parts]);

    return (
        <group>
            {geometries.map((geo, i) => (
                <mesh key={i} geometry={geo}>
                    <meshStandardMaterial
                        color={OBJ_MESH_COLOR}
                        metalness={0}
                        roughness={0.85}
                        side={THREE.DoubleSide}
                        defines={BACKFACE_DEFINES}
                    />
                    {isSelected && <InkEdges isSelected={isSelected} />}
                </mesh>
            ))}
        </group>
    );
}
